function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    // Sorting dropdown logic
    const sortByDropdown = document.getElementById("SortBy");
    if (sortByDropdown) {
      sortByDropdown.addEventListener("change", (event) => {
        const selectedOption = event.target.value;
        const url = new URL(window.location.href);
        url.searchParams.set("sort_by", selectedOption);
        window.location.href = url.toString();
      });
    }

    this.initializeFocusTrap();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop",
    );

    if (countContainer) countContainer.classList.add("loading");
    if (countContainerDesktop) countContainerDesktop.classList.add("loading");

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok)
          throw new Error(`Request failed with status ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        console.error("Facet data fetch failed:", error);
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
        if (typeof initializeMuskPriceRange === "function")
          initializeMuskPriceRange();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
    if (typeof initializeMuskPriceRange === "function")
      initializeMuskPriceRange();
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .querySelectorAll("#ProductGridContainer .scroll-trigger")
      .forEach((el) => el.classList.add("scroll-trigger--cancel"));
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id,
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (el) => !matchesId(el),
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId,
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else if (
        index > 0 &&
        elementToRender.className === facetsToRender[index - 1].className
      ) {
        document
          .getElementById(facetsToRender[index - 1].id)
          .after(elementToRender);
      } else if (
        elementToRender.parentElement &&
        elementToRender.parentElement.id
      ) {
        const parentId = elementToRender.parentElement.id;
        const targetElement = document.querySelector(`#${parentId} .js-filter`);
        if (targetElement) {
          targetElement.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID),
        );
        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details",
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);
        const isTextInput = event.target.getAttribute("type") === "text";
        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    [".active-facets-mobile", ".active-facets-desktop"].forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (activeFacetsElement) {
        document.querySelector(selector).innerHTML =
          activeFacetsElement.innerHTML;
      }
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");
    if (sourceSummary && targetSummary)
      targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeader = target.querySelector(".facets__header");
    const sourceHeader = source.querySelector(".facets__header");
    if (sourceHeader && targetHeader)
      targetHeader.outerHTML = sourceHeader.outerHTML;

    const targetWrap = target.querySelector(".facets-wrap");
    const sourceWrap = source.querySelector(".facets-wrap");
    if (sourceWrap && targetWrap) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden"),
      );
      if (isShowingMore) {
        sourceWrap
          .querySelectorAll(".facets__item.hidden")
          .forEach((el) => el.classList.replace("hidden", "show-more-item"));
      }
      targetWrap.outerHTML = sourceWrap.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetList = target.querySelector(".mobile-facets__list");
    const sourceList = source.querySelector(".mobile-facets__list");
    if (sourceList && targetList) {
      targetList.outerHTML = sourceList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    const newUrl = `${window.location.pathname}?${searchParams}`;
    const state = { searchParams };
    history.pushState(state, "", newUrl); // Updated from replaceState
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form"),
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            [
              "FacetSortForm",
              "FacetFiltersForm",
              "FacetSortDrawerForm",
            ].includes(form.id)
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") === -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }

  // ---------- FOCUS TRAP FIX ----------
  initializeFocusTrap() {
    // Listen for filter button clicks to open drawer
    document.addEventListener("click", (e) => {
      const filterButton = e.target.closest(
        ".collection-filter-gallery-wrapper .product-filter-row .product-more-filter",
      );
      if (filterButton) {
        e.preventDefault();
        this.openFacetsDrawer();
      }
    });

    // Listen for close button clicks
    document.addEventListener("click", (e) => {
      const closeBtn = e.target.closest(
        ".product-filter-sidebar-fixed .cls-btn",
      );
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.closeFacetsDrawer();
      }
    });

    // Listen for clicks outside the sidebar to close
    document.addEventListener("click", (e) => {
      setTimeout(() => {
        const sidebar = document.querySelector(
          ".product-filter-sidebar.product-filter-sidebar-fixed",
        );
        const overlay = document.querySelector(".overlay-custom");
        if (!sidebar || !sidebar.classList.contains("active")) return;

        const filterButton = e.target.closest(
          ".collection-filter-gallery-wrapper .product-filter-row .product-more-filter",
        );
        if (filterButton) return;

        const closeBtn = e.target.closest(
          ".product-filter-sidebar-fixed .cls-btn",
        );
        if (closeBtn) return;

        const isClickInsideSidebar = sidebar.contains(e.target);
        const isClickOnOverlay =
          overlay && (overlay.contains(e.target) || e.target === overlay);

        if (!isClickInsideSidebar || isClickOnOverlay) {
          this.closeFacetsDrawer();
        }
      }, 0);
    });
  }

  openFacetsDrawer() {
    const sidebar = document.querySelector(
      ".product-filter-sidebar.product-filter-sidebar-fixed",
    );
    const overlay = document.querySelector(".fusion-overlay-custom");
    if (!sidebar) return;

    sidebar.classList.add("active");
    if (overlay) overlay.classList.add("overlay-active");
    document.body.classList.add("overflow-hidden");

    setTimeout(() => {
      this.setupFocusTrap();
    }, 50);

    this.escHandler = (event) => {
      if (event.key === "Escape" || event.keyCode === 27) {
        this.closeFacetsDrawer();
      }
    };
    document.addEventListener("keydown", this.escHandler);
  }

  closeFacetsDrawer() {
    const sidebar = document.querySelector(
      ".product-filter-sidebar.product-filter-sidebar-fixed",
    );
    const overlay = document.querySelector(".fusion-overlay-custom");

    if (sidebar) sidebar.classList.remove("active");
    if (overlay) overlay.classList.remove("overlay-active");
    document.body.classList.remove("overflow-hidden");

    this.removeFocusTrap();

    if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler);
      this.escHandler = null;
    }

    // restore focus to trigger button
    const trigger = document.querySelector(
      ".collection-filter-gallery-wrapper .product-filter-row .product-more-filter",
    );
    if (trigger) trigger.focus();
  }

  setupFocusTrap() {
    const sidebar = document.querySelector(
      ".product-filter-sidebar.product-filter-sidebar-fixed",
    );
    if (!sidebar) return;

    const focusContainer =
      sidebar.querySelector(".product-filter-sidebar-inner") || sidebar;
    const closeButton = sidebar.querySelector(".cls-btn");

    if (!focusContainer.hasAttribute("tabindex")) {
      focusContainer.setAttribute("tabindex", "-1");
    }

    const updateFocusable = () => {
      return Array.from(
        focusContainer.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled',
        ),
      ).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null,
      );
    };

    const focusableElements = updateFocusable();
    const firstElement = focusableElements[0] || closeButton || focusContainer;
    const lastElement =
      focusableElements[focusableElements.length - 1] ||
      closeButton ||
      focusContainer;

    this.removeFocusTrap();

    this.focusHandler = (event) => {
      if (event.key !== "Tab") return;

      const focusables = updateFocusable();
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", this.focusHandler);

    (closeButton || firstElement).focus();
  }

  removeFocusTrap() {
    if (this.focusHandler) {
      document.removeEventListener("keydown", this.focusHandler);
      this.focusHandler = null;
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((el) => {
      el.addEventListener("change", this.onRangeChange.bind(this));
      el.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
